import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import styles from "./RecallPaywall.module.scss"

/**
 * RecallPaywall — an original, self-contained, NON-privacy paywall.
 *
 * Divergence brief: every prior paywall pitched privacy/security on a dark,
 * skeuomorphic, centered-hero+card skeleton. Recall deliberately moves every
 * axis:
 *   - Value frame:   SEARCH / FIND  (not privacy) — "find any photo in seconds"
 *   - Layout:        a working search bar over a live photographic GRID
 *   - Interaction:   INPUT-DRIVEN — tap smart-search chips and watch the
 *                    library light up the matches in real time
 *   - Register:      confident-minimal / curious / lightly playful
 *   - Art direction: LIGHT + PHOTOGRAPHIC (real Unsplash photos, not vectors)
 *
 * The hero is the feature performing itself: a phone gallery where searching
 * "beach" or "my dog" instantly highlights the matching shots and dims the
 * rest. The pitch is the demo. Yearly is the prominent price.
 */

type Chip = {
  key: string
  label: string
  query: string
}

// Smart-search chips. `key` doubles as the photo-category prefix in /recall/.
const CHIPS: Chip[] = [
  { key: "all", label: "All photos", query: "" },
  { key: "beach", label: "Beach", query: "beach" },
  { key: "sunset", label: "Sunsets", query: "sunsets" },
  { key: "dog", label: "Dog", query: "my dog" },
  { key: "food", label: "Food", query: "dinner" },
  { key: "mountain", label: "Mountains", query: "mountains" },
  { key: "city", label: "City", query: "city at night" },
  { key: "flower", label: "Flowers", query: "flowers in bloom" },
  { key: "coffee", label: "Coffee", query: "coffee" },
]

const PHOTO_CATS = [
  "beach",
  "sunset",
  "dog",
  "food",
  "mountain",
  "city",
  "flower",
  "coffee",
]

type Photo = { src: string; cat: string }

// 24 local photos (3 per category), kept in a deterministic but visually mixed
// order so the masonry doesn't show category clumps.
const PHOTOS: Photo[] = (() => {
  const grid: Photo[] = []
  for (let i = 1; i <= 3; i++) {
    for (const cat of PHOTO_CATS) {
      grid.push({ src: `/recall/${cat}-${i}.jpg`, cat })
    }
  }
  // light shuffle by interleaving the three passes already done above
  return grid
})()

const RecallPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.max(1, Math.round((1 - ratio) * 100))
  }, [yearlyProduct, monthlyProduct])

  const [active, setActive] = useState<string>("all")
  const [typed, setTyped] = useState<string>("")
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const typeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const activeChip = CHIPS.find((c) => c.key === active) ?? CHIPS[0]
  const matchCount =
    active === "all" ? PHOTOS.length : PHOTOS.filter((p) => p.cat === active).length

  // Typewriter effect in the search field whenever the query changes.
  useEffect(() => {
    const target = activeChip.query
    if (typeTimer.current) clearTimeout(typeTimer.current)
    if (reduceMotion) {
      setTyped(target)
      return
    }
    let i = 0
    setTyped("")
    const tick = () => {
      i += 1
      setTyped(target.slice(0, i))
      if (i < target.length) {
        typeTimer.current = setTimeout(tick, 45)
      }
    }
    typeTimer.current = setTimeout(tick, 90)
    return () => {
      if (typeTimer.current) clearTimeout(typeTimer.current)
    }
  }, [activeChip.query, reduceMotion])

  // Auto-demo: run a "beach" search shortly after load so the feature is shown
  // performing itself even with no interaction.
  useEffect(() => {
    if (reduceMotion) return
    const t = setTimeout(() => setActive("beach"), 1100)
    return () => clearTimeout(t)
  }, [reduceMotion])

  const handleClose = () => native.postNativeEvent("close")
  const handlePurchase = () => {
    const product: Product | undefined =
      plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const selectedHasTrial =
    plan === "yearly"
      ? (yearlyProduct?.hasTrial ?? true)
      : (monthlyProduct?.hasTrial ?? false)

  return (
    <div className={styles.page}>
      <button
        className={styles.close}
        onClick={handleClose}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.spark}>
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <circle
                cx="10.5"
                cy="10.5"
                r="6.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.1"
              />
              <path
                d="M15.4 15.4L21 21"
                stroke="currentColor"
                strokeWidth="2.1"
                strokeLinecap="round"
              />
            </svg>
          </span>
          Recall
          <span className={styles.proTag}>PRO</span>
        </div>
        <h1 className={styles.title}>
          Find any photo
          <br />
          in <span className={styles.accent}>seconds.</span>
        </h1>
        <p className={styles.sub}>
          Search your library by what&rsquo;s actually in it — beaches, your dog,
          last summer&rsquo;s sunsets — even shots from years ago.
        </p>
      </header>

      {/* The feature, performing itself */}
      <section className={styles.demo} aria-label="Search demo">
        <div className={styles.searchBar}>
          <svg
            className={styles.searchIcon}
            viewBox="0 0 24 24"
            width="19"
            height="19"
            aria-hidden
          >
            <circle
              cx="10.5"
              cy="10.5"
              r="6.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.1"
            />
            <path
              d="M15.4 15.4L21 21"
              stroke="currentColor"
              strokeWidth="2.1"
              strokeLinecap="round"
            />
          </svg>
          <span className={styles.searchText}>
            {typed || (
              <span className={styles.placeholder}>Search your photos…</span>
            )}
            {!reduceMotion && <span className={styles.caret} />}
          </span>
          <AnimatePresence mode="wait">
            <motion.span
              key={active}
              className={styles.resultCount}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
            >
              {matchCount} {matchCount === 1 ? "result" : "results"}
            </motion.span>
          </AnimatePresence>
        </div>

        <div className={styles.chips}>
          {CHIPS.map((chip) => (
            <button
              key={chip.key}
              className={`${styles.chip} ${
                active === chip.key ? styles.chipOn : ""
              }`}
              onClick={() => setActive(chip.key)}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div className={styles.grid}>
          {PHOTOS.map((photo, idx) => {
            const searching = active !== "all"
            const match = !searching || photo.cat === active
            const isHit = searching && photo.cat === active
            return (
              <motion.div
                key={photo.src}
                className={`${styles.tile} ${isHit ? styles.tileOn : styles.tileOff}`}
                animate={{
                  opacity: match ? 1 : 0.24,
                  scale: match ? 1 : 0.95,
                  filter: match ? "grayscale(0)" : "grayscale(0.85)",
                }}
                transition={{
                  duration: reduceMotion ? 0 : 0.4,
                  delay: reduceMotion ? 0 : (idx % 8) * 0.012,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <img src={photo.src} alt="" loading="lazy" />
                {isHit && (
                  <motion.span
                    className={styles.checkPill}
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.25, delay: 0.1 }}
                  >
                    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden>
                      <path
                        d="M5 12.5l4.2 4.2L19 7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </motion.span>
                )}
              </motion.div>
            )
          })}
          <div className={styles.gridFade} />
        </div>
      </section>

      {/* Quietly factual capability strip — not the 4-icon perk grid */}
      <ul className={styles.lines}>
        <li>
          <b>On-device search.</b> Photos are indexed privately on your phone —
          nothing is uploaded to find them.
        </li>
        <li>
          <b>Search by content, place &amp; date.</b> &ldquo;beach 2019&rdquo;,
          &ldquo;my dog at the park&rdquo;, &ldquo;receipts&rdquo; — it just
          works.
        </li>
        <li>
          <b>Instant, even at 50,000 photos.</b> Results appear as you type.
        </li>
      </ul>

      {/* Pricing — yearly is the headline */}
      <section className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planOn : ""
          }`}
          onClick={() => setPlan("yearly")}
        >
          <span className={styles.bestBadge}>BEST VALUE · SAVE {savings}%</span>
          <span className={styles.planTop}>
            <span className={styles.planName}>Yearly</span>
            <span className={styles.radio} data-on={plan === "yearly"} />
          </span>
          <span className={styles.bigPrice}>{yearlyPrice}</span>
          <span className={styles.perYear}>per year</span>
          <span className={styles.perMonth}>
            just {yearlyPerMonth}/mo, billed annually
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planOn : ""
          }`}
          onClick={() => setPlan("monthly")}
        >
          <span className={styles.planTop}>
            <span className={styles.planName}>Monthly</span>
            <span className={styles.radio} data-on={plan === "monthly"} />
          </span>
          <span className={styles.midPrice}>{monthlyPrice}</span>
          <span className={styles.perYear}>per month</span>
        </button>
      </section>

      <button className={styles.cta} onClick={handlePurchase}>
        <span className={styles.ctaMain}>
          {selectedHasTrial ? "Try Recall free" : "Continue"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? selectedHasTrial
              ? `7 days free, then ${yearlyPrice}/year`
              : `${yearlyPrice}/year · ${yearlyPerMonth}/mo`
            : `${monthlyPrice}/month`}
        </span>
      </button>

      <div className={styles.footer}>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
      </div>
      <p className={styles.legal}>
        Cancel anytime. Your photos always stay yours — searching simply makes
        them findable.
      </p>
    </div>
  )
}

export default RecallPaywall
